// The ONE mapping from an OGS branch region to the renderer's input-port names.
// The blueprint contract lives in plugins/ogs-renderer/plugin.json — its dataIn
// pins are `geom_uv`/`color_uv` (the primary region) plus one `<region>_geom_uv`
// /`<region>_color_uv` pair per secondary region. A multi-branch OGSRenderer
// needs the same names at runtime, so both sides derive them from here rather
// than each spelling the strings out (tests/unit/branchPorts.test.mjs reads the
// manifest and asserts the agreement).
//
// ONE RULE, NO ALIASES: the primary region is `head`, every other region gets
// ports named after itself. A pin therefore always says which region it carries,
// and two branches can never share a pair — HeadTeethEngine._collect keys its
// inbound map by port name, so a collision drops one region's UVs and lifts the
// other's decode onto its mesh, with nothing downstream able to detect it.
//
// `region` is the scene.json branch key and the bundle owns it: the engine looks
// the branch up by that name, so the blueprint cannot rename a region. The key
// states which branch, not which anatomy — a bundle is free to put the eyes in
// its `body` branch. `eyes` is not available as a mesh region: it names the
// gaze-conditioned branch, whose pins are a GATE rather than a data path.

export type BranchPortPair = readonly [geom: string, color: string];

/** Renderer input-port pair for a branch. `region` is the scene.json branch key
 *  (head / body / teeth / clothes) or the blueprint node's `region` config.
 *  `index` is only a fallback for an unnamed branch, and only at index 0 —
 *  anything later has no name to derive ports from and throws rather than
 *  silently colliding with another branch's ports. */
export function branchPortNames(region?: string | null, index = 0): BranchPortPair {
  const r = String(region ?? "").trim().toLowerCase();
  if (r === "head" || (!r && index === 0)) return ["geom_uv", "color_uv"];
  if (!r) throw new Error(`branchPortNames: unnamed branch at index ${index} — a region name is required`);
  return [`${r}_geom_uv`, `${r}_color_uv`];
}
